import React, { useState } from 'react';
import { RefreshCw, Check, AlertCircle, AlertTriangle } from 'lucide-react';
import { useT } from '../lib/i18n';
import { useLiveSync } from '../hooks/useLiveSync';
import { bumpDataVersion } from '../lib/dataVersion';
import api from '../lib/api';
import { formatDate, formatDateTime, formatNumber, weekdayShort } from '../lib/format';
import { gapLines } from '../lib/syncResult';

/**
 * Top-bar sync button — the one place that says how fresh the figures are.
 *
 * The server refreshes stale data by itself when a page is opened; while that
 * runs the button spins and says so. Pressing it syncs on the spot: at least
 * the last 3 days, further back to the previous successful sync, plus any
 * expected opening day that still has no receipts. When such days are
 * outstanding the button turns amber and names them.
 *
 * Either way the page repaints in place (no browser reload) — a sync bumps the
 * data version and every card re-fetches.
 */
export default function SyncButton() {
  const t = useT();
  const ls = useLiveSync();
  const [state, setState] = useState('idle');   // idle | running | done | error
  const [msg, setMsg] = useState('');

  if (!ls?.connected) return null;
  const gaps = ls.gaps || [];
  const autoRunning = Boolean(ls.syncing) && state === 'idle';
  const busy = state === 'running' || autoRunning;

  const run = async () => {
    if (busy) return;
    setState('running'); setMsg('');
    try {
      const r = await api.lightspeedSync({ days: 3 });
      setMsg([`${formatNumber(r.sales ?? r.orders ?? 0)} ${t('bonnen')}`, ...gapLines(r.gaps, t)].join(' · '));
      setState('done');
      bumpDataVersion();
      setTimeout(() => setState('idle'), 12000);
    } catch (e) {
      setState('error');
      setMsg(e?.response?.data?.detail || t('Sync mislukt'));
      setTimeout(() => setState('idle'), 5000);
    }
  };

  const last = ls.last_sync?.at ? `${t('laatste sync')}: ${formatDateTime(ls.last_sync.at)}` : '';
  const missingLabel = `${gaps.length} ${t(gaps.length === 1 ? 'dag ontbreekt' : 'dagen ontbreken')}`;
  const missing = gaps.length
    ? `${missingLabel}: ${gaps.map((g) => `${weekdayShort(g.weekday_nl)} ${formatDate(g.date)}`).join(', ')}`
    : '';
  const title = [t('LightSpeed nu synchroniseren (sinds de vorige sync, min. 3 dagen; vult ontbrekende dagen aan)'), last, missing]
    .filter(Boolean).join(' · ');

  const warn = state === 'idle' && !autoRunning && gaps.length > 0;
  const Icon = state === 'done' ? Check : state === 'error' ? AlertCircle : warn ? AlertTriangle : RefreshCw;
  const tone = state === 'done' ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
    : state === 'error' ? 'border-red-200 bg-red-50 text-red-700'
    : warn ? 'border-amber-300 bg-amber-50 text-amber-800 hover:border-amber-400'
    : 'border-slate-200 bg-white text-slate-600 hover:border-[#cf9a40] hover:text-[#114817]';
  const label = autoRunning ? t('Data verversen…')
    : state === 'running' ? t('Synchroniseren…')
    : state === 'done' ? `${t('Gesynchroniseerd')} · ${msg}`
    : state === 'error' ? msg
    : warn ? `${t('Sync')} · ${missingLabel}`
    : t('Sync');

  return (
    <button type="button" onClick={run} title={title} aria-label={title} disabled={busy}
      className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[11px] font-medium transition-colors disabled:opacity-70 max-w-[60vw] ${tone}`}>
      <Icon className={`h-3.5 w-3.5 shrink-0 ${busy ? 'animate-spin' : ''}`} />
      <span className={`${autoRunning ? '' : 'hidden sm:inline'} truncate`}>{label}</span>
    </button>
  );
}
